import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE from '../utils/api';

const EVENT_STYLES = {
  exam: { icon: '📝', gradient: 'from-red-500 to-rose-600' },
  holiday: { icon: '🌴', gradient: 'from-green-500 to-emerald-600' },
  meeting: { icon: '🗣️', gradient: 'from-amber-500 to-yellow-600' },
  sports: { icon: '🏆', gradient: 'from-blue-500 to-cyan-600' },
  cultural: { icon: '🎭', gradient: 'from-purple-500 to-pink-600' },
  field_trip: { icon: '🚌', gradient: 'from-orange-500 to-amber-600' },
  other: { icon: '📌', gradient: 'from-indigo-500 to-violet-600' },
};

const daysUntil = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return `In ${diff} days`;
};

const UpcomingEventsScroller = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE}/events`, { params: { upcoming: true } })
      .then(res => setEvents(Array.isArray(res.data) ? res.data.slice(0, 10) : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || events.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          📅 Upcoming Events
        </h2>
        <span className="text-xs text-gray-400">scroll for more →</span>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
        {events.map((event) => {
          const style = EVENT_STYLES[event.eventType] || EVENT_STYLES.other;
          return (
            <div
              key={event._id}
              className={`relative overflow-hidden snap-start flex-shrink-0 w-64 rounded-2xl shadow-lg p-5 text-white bg-gradient-to-br ${style.gradient} hover:scale-[1.03] hover:shadow-xl transition-transform duration-200`}
            >
              <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/10 rounded-full" />
              <div className="absolute -right-2 -bottom-8 w-20 h-20 bg-white/10 rounded-full" />

              <div className="relative flex items-center justify-between mb-3">
                <span className="text-2xl">{style.icon}</span>
                <span className="px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-[11px] font-semibold uppercase tracking-wide">
                  {event.eventType?.replace('_', ' ') || 'other'}
                </span>
              </div>

              <h3 className="relative font-bold text-base leading-snug mb-1 line-clamp-2">
                {event.title}
              </h3>
              <p className="relative text-xs text-white/80 line-clamp-2 mb-3 min-h-[2rem]">
                {event.description}
              </p>

              <div className="relative flex items-center justify-between text-xs">
                <span className="font-medium">
                  {new Date(event.eventDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </span>
                <span className="px-2 py-1 bg-white text-gray-800 rounded-full font-bold shadow-sm">
                  {daysUntil(event.eventDate)}
                </span>
              </div>

              {event.isSchoolWide && (
                <span className="relative inline-block mt-2 text-[10px] bg-white/20 px-2 py-0.5 rounded-full">
                  🏫 School-wide
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpcomingEventsScroller;
